import { captureException } from "@sentry/react";
import {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  type AxiosInstance,
  isCancel,
} from "axios";
import { EventEmitter } from "events";
import { hasErrorCode } from "~/api/apiError";
import {
  AuthCooldownError,
  AuthError,
  AuthExistingError,
  AuthExpiredError,
  AuthNotFoundError,
} from "~/api/auth/AuthErrors";
import { jwtTokenToAuthUser, type AuthUser } from "~/api/auth/AuthUser";
import * as authApi from "~/api/generated/auth/auth";
import {
  type AuthLoginRequest,
  type AuthRegisterRequest,
  ErrorCode,
} from "~/api/generated/model";
import type { I18N } from "~/i18n";
import { type WithRequired } from "~/utils/types";

const REFRESH_TOKEN_STATUS_CODES = [401, 403];
const AUTO_TOKEN_REFRESH_OFFSET = 30_000; // 1/2 a minute

// extend Axios types, to hold a __retry indicator
type AuthRequestFlags = {
  __isRetry?: boolean;
  // A rejected refresh must not wait on a refresh, which would be itself.
  __isRefresh?: boolean;
};
type CustomInternalAxiosRetryRequestConfig = InternalAxiosRequestConfig &
  AuthRequestFlags;
type CustomAxiosError = AxiosError & {
  config: CustomInternalAxiosRetryRequestConfig | undefined;
};
type ReturnUrl = string | undefined;

type AuthEventMap = {
  stateChange: [AuthUser, AuthUser];
  impersonationExpired: [];
  impersonationActionBlocked: [];
};

export class AuthClient extends EventEmitter<AuthEventMap> {
  #axiosInstance: AxiosInstance;
  #t: I18N["t"];
  #axiosAuthResponseInterceptor: number | null = null;

  #initialRefreshPromise: Promise<boolean>;
  #currentRefreshPromise: Promise<boolean> | null = null;
  #refreshTimer: number | null = null;
  #tokenGeneration = 0;

  #user: AuthUser = null;

  constructor(axiosInstance: AxiosInstance, i18nInstance: I18N) {
    super();
    this.#axiosInstance = axiosInstance;
    this.#t = i18nInstance.t;

    // initialize the first refresh
    this.#initialRefreshPromise = this.refreshToken();
  }

  private internalRefreshToken = async () => {
    const generation = this.#tokenGeneration;
    try {
      const refreshRequest: AxiosRequestConfig & AuthRequestFlags = {
        // Seems like the API check the Authorization header, and responds with 403 before checking the refresh token.
        headers: { Authorization: undefined },
        __isRefresh: true,
      };
      const res = await authApi.refresh(refreshRequest);
      // A token was installed while this refresh was in flight - impersonation started or
      // stopped - so this response is stale: the refresh cookie is the staff user's own, and
      // installing it now would silently switch identity. The newer token stands, hence true.
      if (this.#tokenGeneration !== generation) {
        return true;
      }
      if (res.access_token) {
        this.accessToken = res.access_token;
      }

      return true;
    } catch (err) {
      /* 
      Block the user on network error - this indicated the API is down.
      Block the user on 503 - Service Unavailable.
       */
      if (
        err instanceof AxiosError &&
        (err.code === AxiosError.ERR_NETWORK || err.status === 503)
      ) {
        throw err;
      }
      return false;
    }
  };

  private set accessToken(token: string | null) {
    this.#tokenGeneration++;

    // When setting an accessToken, clear current timer.
    // Once we've parsed the user, a new one is established.
    if (this.#refreshTimer) {
      window.clearTimeout(this.#refreshTimer);
      this.#refreshTimer = null;
    }

    const authorizationHeaderValue = getAccessTokenBearerString(token);

    this.#axiosInstance.defaults.headers.common.Authorization =
      authorizationHeaderValue;

    // eject previous handler
    if (this.#axiosAuthResponseInterceptor !== null) {
      this.#axiosInstance.interceptors.response.eject(
        this.#axiosAuthResponseInterceptor,
      );
    }
    // attach axios 401 handler
    this.#axiosAuthResponseInterceptor =
      this.#axiosInstance.interceptors.response.use(
        undefined,
        async (error: CustomAxiosError) => {
          // A canceled request carries no response, which would otherwise read as a 401.
          if (isCancel(error)) return Promise.reject(error);
          const originalRequest = error.config;
          const status = error.response?.status ?? 401;

          // Buttons are never pre-disabled, so a refusal is explained when it arrives.
          if (hasErrorCode(error, ErrorCode.IMPERSONATION_DENIED)) {
            this.emit("impersonationActionBlocked");
            return Promise.reject(error);
          }

          if (
            originalRequest &&
            REFRESH_TOKEN_STATUS_CODES.includes(status) &&
            !originalRequest.__isRetry &&
            !originalRequest.__isRefresh
          ) {
            // Refreshing during impersonation would restore the staff user's own token behind
            // their back, so the request is left to fail. Only a 401 means the impersonation
            // token itself is gone; a 403 is an ordinary authorization failure.
            if (this.#user?.isImpersonated) {
              // A network failure carries no response; it must not end the session.
              if (error.response?.status === 401) {
                this.emit("impersonationExpired");
              }
              return Promise.reject(error);
            }

            // mark as retry, to avoid infinite failed calls
            originalRequest.__isRetry = true;

            const userIdBefore = this.#user?.id;

            // await token refresh (attempt as few simultaneous request at a time)
            await (this.#currentRefreshPromise ?? this.refreshToken());

            // Impersonation may have started while the refresh was pending; replaying the
            // request with the instance's token would then send it as the impersonated user.
            if (this.#user?.id !== userIdBefore || this.#user?.isImpersonated) {
              return Promise.reject(error);
            }

            // The rejected request still carries the old token, and this handler's closure holds
            // it too; the refresh installed the new one on the instance defaults.
            originalRequest.headers.Authorization =
              this.#axiosInstance.defaults.headers.common.Authorization;

            // rerun original request
            return this.#axiosInstance(originalRequest);
          }

          return Promise.reject(error);
        },
      );

    // store the previous user for stateChange event.
    const previousUser = this.#user ? { ...this.#user } : null;

    this.#user = jwtTokenToAuthUser(token);

    // If we successfully parsed the user, establish a new timer for renewal.
    if (this.#user) {
      const expiresInMs = this.#user.token.expireAt.getTime() - Date.now();

      // Safe guard for if we in development tests low expire times.
      const timeoutInMs =
        expiresInMs > AUTO_TOKEN_REFRESH_OFFSET
          ? expiresInMs - AUTO_TOKEN_REFRESH_OFFSET
          : expiresInMs;

      // Only set refresh timeout for a positive timeout.
      if (expiresInMs > 0) {
        // An impersonated session is never renewed: it ends when the token expires, and the
        // staff user is told so rather than being switched back to their own identity.
        this.#refreshTimer = this.#user.isImpersonated
          ? window.setTimeout(() => {
              this.emit("impersonationExpired");
            }, expiresInMs)
          : window.setTimeout(() => {
              this.refreshToken().catch((err) => {
                // The timer fires the moment a sleeping laptop wakes, usually before the network is
                // back. The next request's 401 refreshes again, so the failure is harmless.
                if (
                  err instanceof AxiosError &&
                  err.code === AxiosError.ERR_NETWORK
                ) {
                  return;
                }
                captureException(err);
                console.error(err);
              });
            }, timeoutInMs);
      }
    }

    this.emit("stateChange", this.#user, previousUser);
  }

  public get user() {
    return this.#user;
  }

  public get initialRefreshPromise() {
    return this.#initialRefreshPromise;
  }

  public getFreshAuthorization = async (): Promise<string> => {
    await this.#initialRefreshPromise;
    if (this.#currentRefreshPromise) await this.#currentRefreshPromise;
    if (!this.#user) throw new Error("Authentication required");
    const expiresIn = this.#user.token.expireAt.getTime() - Date.now();
    if (this.#user.isImpersonated && expiresIn <= 0)
      throw new Error("Impersonation expired");
    if (!this.#user.isImpersonated && expiresIn < AUTO_TOKEN_REFRESH_OFFSET) {
      if (!(await this.refreshToken()))
        throw new Error("Authentication expired");
    }
    const authorization =
      this.#axiosInstance.defaults.headers.common.Authorization;
    if (typeof authorization !== "string")
      throw new Error("Authentication required");
    return authorization;
  };

  public signup = async (request: AuthRegisterRequest) => {
    try {
      const res = await authApi.register(request);
      if (res.access_token) {
        this.accessToken = res.access_token;
      }

      return this.user;
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.signup",
        caseHandler: (axiosError) => {
          if (hasErrorCode(axiosError, ErrorCode.PHONE_NUMBER_IN_USE)) {
            throw new AuthExistingError(
              this.#t("auth:register.errors.phone_taken"),
            );
          }

          if (hasErrorCode(axiosError, ErrorCode.INVALID_PHONE_NUMBER)) {
            throw new AuthError(this.#t("auth:register.errors.invalid_phone"));
          }

          if (hasErrorCode(axiosError, ErrorCode.EMAIL_DOMAIN_NOT_ALLOWED)) {
            throw new AuthError(
              this.#t("auth:register.errors.not_permitted_domain"),
            );
          }

          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthExistingError(
              this.#t("auth:register.errors.username_taken"),
            );
          }
        },
      });
    }
  };

  public login = async (request: AuthLoginRequest) => {
    try {
      const res = await authApi.login(request);
      if (res.access_token) {
        this.accessToken = res.access_token;
      }

      return this.user;
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.login",
        caseHandler: (axiosError) => {
          if (hasErrorCode(axiosError, ErrorCode.EMAIL_DOMAIN_NOT_ALLOWED)) {
            throw new AuthError(
              this.#t("auth:log_in.errors.not_permitted_domain"),
            );
          }
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthNotFoundError(
              this.#t("auth:log_in.errors.not_found"),
            );
          }
        },
      });
    }
  };

  public refreshToken = async () => {
    this.#currentRefreshPromise = this.internalRefreshToken();
    try {
      return await this.#currentRefreshPromise;
    } finally {
      this.#currentRefreshPromise = null;
    }
  };

  /* /auth/impersonate */
  public impersonate = async (email: string) => {
    try {
      const res = await authApi.impersonate({ email });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }

      return this.user;
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.impersonate",
        caseHandler: (axiosError) => {
          if (axiosError.status === 404) {
            throw new AuthNotFoundError(
              this.#t("staff:impersonation.errors.not_found"),
            );
          }
        },
      });
    }
  };

  /**
   * Ends an impersonated session by refreshing from the staff user's own cookie, which was left
   * untouched while impersonating.
   */
  public stopImpersonating = async () => {
    await this.refreshToken();
    // The refresh reports whether the call succeeded, not whose token is installed, so the
    // identity itself decides: still impersonated means the staff user's own session is gone,
    // and ending it beats leaving them on the impersonated identity.
    if (this.#user?.isImpersonated) {
      this.accessToken = null;
    }

    return this.user;
  };

  public logout = async () => {
    try {
      await authApi.logout();

      this.accessToken = null;
    } catch (err) {
      this.#apiErrorHandler(err, { caller: "AuthClient.logout" });
    }
  };

  /* /auth/verify/email */
  public verifyEmail = async (token: string) => {
    try {
      const res = await authApi.verifyEmail({ secret: token });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.verifyEmail",
        caseHandler: (axiosError) => {
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthExpiredError(
              this.#t("auth:email_verification.errors.token_expired"),
            );
          }
        },
      });
    }
  };

  /* /auth/verify/ott (one-time-token) */
  public verifyMfaCode = async (code: string) => {
    try {
      const res = await authApi.verifyOtt({ secret: code });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.verifyMfaCode",
        caseHandler: (axiosError) => {
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthExpiredError(this.#t("auth:mfa.errors.wrong_code"));
          }
          // TODO: how do we determine the token to be expired - I'm unsure if refreshToken works in this scenario.
          // We default to wrong code, as expire-text won't currently be an option.
          // throw new AuthExpiredError(this.#t("auth:mfa.errors.token_expired"));
        },
      });
    }
  };

  /* /auth/verify/password */
  public updatePassword = async (token: string, newPassword: string) => {
    try {
      const res = await authApi.updatePassword({
        secret: token,
        newPassword,
      });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.updatePassword",
        caseHandler: (axiosError) => {
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthExpiredError(
              this.#t("auth:reset_password.form.errors.token_expired"),
            );
          }
        },
      });
    }
  };

  /* /auth/verify/phone_verification */
  public verifyPhoneCode = async (code: string) => {
    try {
      const res = await authApi.verifyPhone({ secret: code });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.verifyPhoneCode",
        caseHandler: (axiosError) => {
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthExpiredError(
              this.#t("auth:sms_verification.errors.wrong_code"),
            );
          }
        },
      });
    }
  };

  /* /auth/send/phone_verification */
  public requestPhoneVerificationCode = async (returnUrl: ReturnUrl) => {
    try {
      const res = await authApi.resendPhoneVerification({ returnUrl });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.requestPhoneVerificationCode",
        caseHandler: (axiosError) => {
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthCooldownError(
              this.#t("auth:sms_verification.errors.cooldown_not_met"),
            );
          }
        },
      });
    }
  };

  /* /auth/resend/email */
  public requestEmailVerificationEmail = async (returnUrl: ReturnUrl) => {
    try {
      const res = await authApi.resendEmail({ returnUrl });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.requestEmailVerificationEmail",
        caseHandler: (axiosError) => {
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthCooldownError(
              this.#t("auth:email_verification.errors.cooldown_not_met"),
            );
          }
        },
      });
    }
  };

  /* /auth/resend/ott (one-time-token) */
  public requestMfaCodeEmail = async () => {
    try {
      const res = await authApi.resendOtt({});
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.requestMfaCodeEmail",
        caseHandler: (axiosError) => {
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthCooldownError(
              this.#t("auth:mfa.errors.cooldown_not_met"),
            );
          }
        },
      });
    }
  };

  /* /auth/resend/password */
  public requestResetPasswordEmail = async (
    email: string,
    returnUrl: string | undefined,
  ) => {
    try {
      const res = await authApi.resendPassword({ email, returnUrl });
      if (res.access_token) {
        this.accessToken = res.access_token;
      }
    } catch (err) {
      this.#apiErrorHandler(err, {
        caller: "AuthClient.requestResetPasswordEmail",
        caseHandler: (axiosError) => {
          // TODO: Is this the only error type API will return? (missing API spec)
          if (isAxiosErrorInStatusRange(axiosError, [400, 500])) {
            throw new AuthCooldownError(
              this.#t("auth:forgot_password.errors.cooldown_not_met"),
            );
          }
        },
      });
    }
  };

  #apiErrorHandler = (
    err: unknown,
    {
      caller,
      caseHandler,
      fallbackError,
    }: {
      caller: string;
      caseHandler?: (err: AxiosErrorWithStatus) => void;
      fallbackError?: AuthError;
    },
  ) => {
    conditionalCaptureException(err, caller);

    if (caseHandler && isAxiosErrorWithStatus(err)) {
      caseHandler(err);
    }

    throw (
      fallbackError ?? new AuthError(this.#t("auth:generic.errors.unknown"))
    );
  };
}

const getAccessTokenBearerString = (accessToken: string | null | undefined) =>
  accessToken ? `Bearer ${accessToken}` : undefined;

type AxiosErrorWithStatus = WithRequired<AxiosError, "status">;

/**
 * Checks if an error is an AxiosError
 */
const isAxiosError = (err: unknown): err is AxiosError =>
  err instanceof AxiosError;

/**
 * Checks if an error is an AxiosError with a status
 */
const isAxiosErrorWithStatus = (err: unknown): err is AxiosErrorWithStatus =>
  isAxiosError(err) && typeof err.status !== "undefined";

/**
 * Check if an AxiosErrorWithStatus is within a min/max range (min is included in the range, max is not).
 */
const isAxiosErrorInStatusRange = (
  err: AxiosErrorWithStatus,
  [min, max]: [min: number, max: number],
) => err.status >= min && err.status < max;

const conditionalCaptureException = (err: unknown, caller: string) => {
  if (!isAxiosErrorWithStatus(err) || err.status >= 500) {
    captureException(err, {
      level: "fatal",
      tags: { message: `Caller: ${caller}` },
    });
  }
};
